import {useMemo} from 'react';
import {useQuery} from '@apollo/client';
import {GET_ASSET_CLASSES} from 'graphql/queries/assetManagement';
import {
  assetClassLabel,
  instrumentKeyLabel,
  instrumentKeysFor,
} from 'pages/AssetManagement/modules/instrumentKeys';
import {isGqlError} from './constants';

/**
 * The asset classes a theme can actually be built on.
 *
 * `assetClassKey` and `instrumentKey` arrive from the filter-structure endpoint
 * as raw enum members, so the form offered every member of `AssetClassKey` —
 * including the six the platform has never created an asset class for — and
 * every member of `InstrumentKey` regardless of the class chosen. An admin
 * could pick Commodities + Bond and save a theme that resolves to nothing.
 *
 * This is the same source of truth the Asset Classes tab reads: the created
 * classes from `getAssetClasses`, and the instrument types each class holds
 * from `instrumentKeysFor`.
 */
export const useAssetClassOptions = enumValues => {
  const {data, loading} = useQuery(GET_ASSET_CLASSES, {
    // Reference data for the session, and the same query the asset class list
    // runs — so opening a theme after that page costs nothing.
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const result = data?.getAssetClasses;

  const options = useMemo(() => {
    if (isGqlError(result)) return [];
    const allowed = enumValues?.length ? enumValues : null;

    const seen = [];
    return (result?.data || [])
      .filter(assetClass => {
        const key = assetClass?.key;
        // A class whose key the filter input would reject is worse than absent:
        // it looks selectable and fails on save.
        if (!key || seen.indexOf(key) !== -1) return false;
        if (allowed && allowed.indexOf(key) === -1) return false;
        seen.push(key);
        return true;
      })
      .map(assetClass => ({
        value: assetClass.key,
        label:
          assetClass.status && assetClass.status !== 'ENABLED'
            ? `${assetClass.name || assetClassLabel(assetClass.key)} · ${String(
                assetClass.status,
              ).toLowerCase()}`
            : assetClass.name || assetClassLabel(assetClass.key),
      }));
  }, [result, enumValues]);

  return {options, loading};
};

/**
 * The instrument types that live under one asset class, as select options.
 *
 * Equities holds public equities and IPOs; fixed income holds bonds; funds hold
 * mutual funds. Labels are the singular instrument name, since the field is
 * asking for one type rather than naming a collection of them.
 */
export const instrumentTypeOptions = (assetClassKey, enumValues) => {
  if (!assetClassKey) return [];
  const allowed = enumValues?.length ? enumValues : null;
  return instrumentKeysFor(assetClassKey)
    .filter(type => !allowed || allowed.indexOf(type.key) !== -1)
    .map(type => ({value: type.key, label: instrumentKeyLabel(type.key)}));
};
